import { Box, Collapse, IconButton, ListItemButton, Stack, TableCell, TableRow } from "@mui/material"
import { Bound } from "lib/@components/binding/Bound"
import { TruncatedTypography } from "lib/@components/truncated-typography"
import Iconify from "minimals-template/components/Iconify"
import { useState } from "react"
import { If } from "lib/@components/switch"
import { ExpandSchedule } from "../group/expand-schedule"
import { SelectItemCheckbox } from "../group/lib/select-item-checkbox"
import { BlockClicks } from "lib/@components/block-clicks"
import { EditScheduleButton } from "./edit-schedule-button"
import { ContextMenuBar } from "lib/@components/context-menu-bar"
import { useParams } from "react-router"
import { openProjectSchedule } from "../utilities/project-navigation"
import { ScheduleSummary } from "routes/schedule/plugins/ai-summary/ai-summary.runtime"
import { setDraftStatusBadgeColor } from "./lib/set-draft-status-badge-color"
import { BadgeLabel } from "routes/regime/plugins/regime-title/badge-label"
import { Else } from "lib/@components/else"
import { useDemand } from "lib/authorization/use-demand"
import { AUTHORING_REVIEW_SCHEDULES_DEMAND } from "authoring/lib/constants/authoring-permissions.mjs"

export function ScopedScheduleRow({ schedule, selectable = true }) {
    const { projectId } = useParams()
    const { title, code, id, tasks, status } = schedule
    const taskCount = tasks?.filter(({ isHeader }) => !isHeader)?.length ?? 0
    const [showMore, setShowMore] = useState(false)
    const badgeColor = setDraftStatusBadgeColor(status)
    const canAccessReview = useDemand(AUTHORING_REVIEW_SCHEDULES_DEMAND)
    return (
        <Bound
            targetId={id}
            schedule={schedule}
            taskCount={taskCount}
            selectedId={id}
            status={status}
            projectId={projectId}
            canAccessReview={canAccessReview}
        >
            <TableRow data-cy="scoped-schedule-row" hover sx={{ cursor: "pointer" }}>
                <If truthy={selectable}>
                    <TableCell sx={{ width: 50, maxWidth: 50, whiteSpace: "noWrap" }}>
                        <BlockClicks>
                            <SelectItemCheckbox />
                        </BlockClicks>
                    </TableCell>
                </If>
                <TableCell sx={{ whiteSpace: "noWrap", maxWidth: { md: 450 } }}>
                    <ListItemButton onClick={handleScheduleTitleClick} data-cy="schedule-title-button">
                        <Stack direction={"row"} alignItems={"center"} spacing={1}>
                            <Box>
                                <Iconify icon={"quill:paper"} sx={{ width: 25, height: 25 }} />
                            </Box>
                            <TruncatedTypography>{title}</TruncatedTypography>
                            <ScheduleSummary id={id} />
                        </Stack>
                    </ListItemButton>
                </TableCell>

                <TableCell sx={{ width: 100, maxWidth: 100, whiteSpace: "noWrap", py: 0 }}>#{code}</TableCell>
                <TableCell
                    sx={{ width: 70, maxWidth: 70, whiteSpace: "noWrap", py: 0 }}
                    align="right"
                    data-cy="tasks-count"
                >
                    {taskCount}
                </TableCell>
                <TableCell sx={{ width: 70, maxWidth: 70, whiteSpace: "noWrap", py: 0 }}>
                    <If truthy={status}>
                        <BadgeLabel variant="subtitle2" sx={{ zoom: 0.8 }} color={badgeColor}>
                            {status?.titleize()}
                        </BadgeLabel>
                        <Else>-</Else>
                    </If>
                </TableCell>
                <TableCell sx={{ width: 70, maxWidth: 70, whiteSpace: "noWrap", py: 0 }}>
                    <EditScheduleButton />
                </TableCell>
                <TableCell sx={{ width: 50, maxWidth: 50, whiteSpace: "noWrap", py: 0 }}>
                    <IconButton
                        aria-label={showMore ? "Hide schedule details" : "Show schedule details"}
                        data-cy="show-schedule-details"
                        onClick={() => setShowMore((show) => !show)}
                    >
                        <Iconify icon={showMore ? "mingcute:up-fill" : "mingcute:down-fill"} />
                    </IconButton>
                </TableCell>
                <TableCell sx={{ width: 70, maxWidth: 70, whiteSpace: "noWrap", py: 0 }}>
                    <ContextMenuBar type="scopedSchedule" />
                </TableCell>
            </TableRow>
            <TableRow>
                <TableCell colSpan={selectable ? 8 : 7} sx={{ p: 0 }}>
                    <Box sx={{ overflow: "hidden" }}>
                        <Collapse in={showMore} mountOnEnter sx={{ width: 1 }}>
                            <If truthy={!!schedule.id}>
                                <ExpandSchedule expandedSchedule={schedule} />
                            </If>
                        </Collapse>
                    </Box>
                </TableCell>
            </TableRow>
        </Bound>
    )

    function handleScheduleTitleClick() {
        openProjectSchedule({ projectId, scheduleId: id, status, canAccessReview })
    }
}
